import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tabs,
  Tab,
  Stack,
} from '@mui/material';
import {
  PointOfSale,
  Receipt,
  ReceiptLong,
  CreditCard,
  TableRestaurant,
  PhoneAndroid,
  Storefront,
  Print,
  Restaurant as RestaurantIcon,
  Payments,
  Tv,
  ViewColumn,
  QrCodeScanner,
  CardMembership,
  CheckCircle,
  ExpandMore,
  Bolt,
  LocalShipping,
  NavigateBefore,
  NavigateNext,
} from '@mui/icons-material';
import Header from './components/Header';
import Footer from './components/Footer';
import PublicAiChatWidget from './components/PublicAiChatWidget';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_GRADIENTS } from '../../config/brand';
import { usePublicRegistrationFlow } from '../../contexts/PublicRegistrationFlowContext';

const HERO_SLIDES_AUTO_MS = 5000;

const HERO_DEVICE_SLIDES = [
  { src: '/assets/slider_kasa.png', titleKey: 'salesSystems.modules.quickSale.title' },
  { src: '/assets/restoran_iphone.png', titleKey: 'salesSystems.modules.restaurant.title' },
  { src: '/assets/paket_yonetimi_pc.png', titleKey: 'salesSystems.modules.packageManagement.title' },
  { src: '/assets/pavo-n86.png', title: 'PavoPay POS' },
];

// ============ STİLLER (Kiosk/QRMenu ile aynı şablon) ============
const PageContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  background: BRAND_GRADIENTS.primary,
}));

const ContentWrapper = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
});

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(13, 71, 161, 0.9) 0%, rgba(25, 118, 210, 0.8) 100%)',
  padding: theme.spacing(10, 0),
  minHeight: '42vh',
  display: 'flex',
  alignItems: 'center',
  textAlign: 'center',
  color: 'white',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.3,
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[8],
  },
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
}));

const StatsSection = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
  border: '1px solid rgba(255, 255, 255, 0.2)',
}));

const ScrollProgressBar = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '4px',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  zIndex: 1001,
  backdropFilter: 'blur(10px)',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    background: BRAND_GRADIENTS.horizontal,
    transition: 'width 0.15s ease-out',
    boxShadow: '0 0 15px rgba(13, 71, 161, 0.6)',
    borderRadius: '0 2px 2px 0',
  },
}));

// ============ ENVANTER ============

type FiscalBrandKey =
  | 'huginT300'
  | 'inposM530'
  | 'bekoX30TR'
  | 'beko300TR'
  | 'pavopay'
  | 'ingenico5000F';

interface FiscalBrand {
  key: FiscalBrandKey;
  anchorId: string;
  icon: React.ReactNode;
  color: string;
}

const FISCAL_BRANDS: FiscalBrand[] = [
  { key: 'huginT300', anchorId: 'brand-huginT300', icon: <PointOfSale />, color: '#1565C0' },
  { key: 'inposM530', anchorId: 'brand-inposM530', icon: <Receipt />, color: '#2E7D32' },
  { key: 'bekoX30TR', anchorId: 'brand-bekoX30TR', icon: <ReceiptLong />, color: '#ED6C02' },
  { key: 'beko300TR', anchorId: 'brand-beko300TR', icon: <ReceiptLong />, color: '#9C27B0' },
  { key: 'pavopay', anchorId: 'brand-pavopay', icon: <CreditCard />, color: '#6A1B9A' },
  { key: 'ingenico5000F', anchorId: 'brand-ingenico5000F', icon: <PointOfSale />, color: '#D32F2F' },
];

const FISCAL_BRAND_MAP: Record<FiscalBrandKey, FiscalBrand> = FISCAL_BRANDS.reduce(
  (acc, b) => ({ ...acc, [b.key]: b }),
  {} as Record<FiscalBrandKey, FiscalBrand>,
);

const FAQ_BRANDS: FiscalBrand[] = [
  FISCAL_BRAND_MAP.pavopay,
  ...FISCAL_BRANDS.filter((brand) => brand.key !== 'pavopay'),
];

type PavoModeKey = 'order' | 'quickSale' | 'restaurant';

interface PavoMode {
  key: PavoModeKey;
  icon: React.ReactNode;
  color: string;
}

const PAVO_MODES: PavoMode[] = [
  { key: 'order', icon: <PhoneAndroid />, color: '#1565C0' },
  { key: 'quickSale', icon: <Storefront />, color: '#2E7D32' },
  { key: 'restaurant', icon: <TableRestaurant />, color: '#ED6C02' },
];

const PAVO_MODE_MAP: Record<PavoModeKey, PavoMode> = PAVO_MODES.reduce(
  (acc, m) => ({ ...acc, [m.key]: m }),
  {} as Record<PavoModeKey, PavoMode>,
);

type PeripheralKey =
  | 'thermalPrinter'
  | 'kitchenPrinter'
  | 'cashDrawer'
  | 'customerDisplay'
  | 'poleDisplay'
  | 'barcodeScanner'
  | 'mealCard'
  | 'menuBoard'
  | 'kioskPanel'
  | 'handTerminal';

interface Peripheral {
  key: PeripheralKey;
  icon: React.ReactNode;
  color: string;
}

const PERIPHERALS: Peripheral[] = [
  { key: 'thermalPrinter', icon: <Print />, color: '#1565C0' },
  { key: 'kitchenPrinter', icon: <RestaurantIcon />, color: '#D32F2F' },
  { key: 'cashDrawer', icon: <Payments />, color: '#2E7D32' },
  { key: 'customerDisplay', icon: <Tv />, color: '#9C27B0' },
  { key: 'poleDisplay', icon: <ViewColumn />, color: '#ED6C02' },
  { key: 'barcodeScanner', icon: <QrCodeScanner />, color: '#6A1B9A' },
  { key: 'mealCard', icon: <CardMembership />, color: '#0288D1' },
  { key: 'menuBoard', icon: <Tv />, color: '#1976D2' },
  { key: 'kioskPanel', icon: <PointOfSale />, color: '#00897B' },
  { key: 'handTerminal', icon: <PhoneAndroid />, color: '#5E35B1' },
];

const PERIPHERAL_MAP: Record<PeripheralKey, Peripheral> = PERIPHERALS.reduce(
  (acc, p) => ({ ...acc, [p.key]: p }),
  {} as Record<PeripheralKey, Peripheral>,
);

// ============ MODÜLLER ============

type ModuleKey = 'quickSale' | 'restaurant' | 'packageManagement';

interface ModuleDef {
  key: ModuleKey;
  anchorId: string;
  icon: React.ReactNode;
  color: string;
  hasPavoModes?: boolean;
}

const MODULES: ModuleDef[] = [
  {
    key: 'quickSale',
    anchorId: 'module-quickSale',
    icon: <Bolt />,
    color: '#1565C0',
  },
  {
    key: 'restaurant',
    anchorId: 'module-restaurant',
    icon: <RestaurantIcon />,
    color: '#ED6C02',
    hasPavoModes: true,
  },
  {
    key: 'packageManagement',
    anchorId: 'module-packageManagement',
    icon: <LocalShipping />,
    color: '#2E7D32',
  },
];

// ============ COMPONENT ============

const SalesSystems: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { openRoleSelection } = usePublicRegistrationFlow();
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const [faqTab, setFaqTab] = React.useState<FiscalBrandKey>('pavopay');
  const [heroSlideIndex, setHeroSlideIndex] = React.useState(0);
  const [heroImageVisible, setHeroImageVisible] = React.useState(true);
  const [heroInteractionTick, setHeroInteractionTick] = React.useState(0);
  const heroSwapTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setScrollProgress(scrollPercent);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    const resolveSectionId = () => {
      const hashCandidates = [location.hash, window.location.hash].filter(Boolean);

      for (const rawHash of hashCandidates) {
        const normalized = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
        const hashParts = normalized.split('#').filter(Boolean);
        const lastPart = hashParts.length > 1 ? hashParts[hashParts.length - 1] : hashParts[0];
        if (!lastPart) continue;

        const decoded = decodeURIComponent(lastPart).split('?')[0].replace(/^\/+/, '');
        const routeStripped = decoded.replace(/^sales-systems\/?/, '');
        const candidates = [decoded, routeStripped];

        for (const candidate of candidates) {
          if (candidate && candidate !== 'sales-systems') return candidate;
        }
      }
      return '';
    };

    const sectionId = resolveSectionId();
    if (!sectionId) return;

    const scrollToHashSection = (attempt = 0) => {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (attempt < 24) {
        window.setTimeout(() => scrollToHashSection(attempt + 1), 180);
      }
    };

    // Mobile render timing can be slower; run immediate + delayed retry.
    scrollToHashSection();
    window.setTimeout(() => scrollToHashSection(), 260);
  }, [location.hash, location.pathname]);

  const changeHeroSlide = (nextIndex: number, isManual = false) => {
    if (nextIndex === heroSlideIndex) return;
    if (isManual) setHeroInteractionTick((prev) => prev + 1);
    if (heroSwapTimeoutRef.current) window.clearTimeout(heroSwapTimeoutRef.current);
    setHeroImageVisible(false);
    heroSwapTimeoutRef.current = window.setTimeout(() => {
      setHeroSlideIndex(nextIndex);
      setHeroImageVisible(true);
      heroSwapTimeoutRef.current = null;
    }, 220);
  };

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      const nextIndex = (heroSlideIndex + 1) % HERO_DEVICE_SLIDES.length;
      changeHeroSlide(nextIndex);
    }, HERO_SLIDES_AUTO_MS);
    return () => window.clearTimeout(timer);
  }, [heroSlideIndex, heroInteractionTick]);

  React.useEffect(() => {
    return () => {
      if (heroSwapTimeoutRef.current) window.clearTimeout(heroSwapTimeoutRef.current);
    };
  }, []);

  const goToPrevHeroSlide = () => {
    const nextIndex = (heroSlideIndex - 1 + HERO_DEVICE_SLIDES.length) % HERO_DEVICE_SLIDES.length;
    changeHeroSlide(nextIndex, true);
  };

  const goToNextHeroSlide = () => {
    const nextIndex = (heroSlideIndex + 1) % HERO_DEVICE_SLIDES.length;
    changeHeroSlide(nextIndex, true);
  };

  const getHeroSlideTitle = (slide: (typeof HERO_DEVICE_SLIDES)[number]) => {
    if (slide.title) return slide.title;
    if (slide.titleKey) return t(slide.titleKey);
    return '';
  };

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const stats = [
    { number: '3', label: t('salesSystems.modules.title').split(',')[0] },
    { number: '6', label: t('salesSystems.stats.fiscalBrands') },
    { number: '12+', label: t('salesSystems.stats.deviceModels') },
    { number: '7/24', label: t('salesSystems.stats.support') },
  ];

  // ---------- MODÜL BÖLÜMÜ RENDER ----------
  const renderModuleSection = (mod: ModuleDef) => {
    const moduleHeroImageMap: Partial<Record<ModuleKey, string>> = {
      quickSale: '/assets/slider_kasa.png',
      restaurant: '/assets/restoran_iphone.png',
      packageManagement: '/assets/paket_yonetimi_pc.png',
    };
    const moduleHeroImage = moduleHeroImageMap[mod.key];
    const moduleBackgroundMap: Record<ModuleKey, string> = {
      quickSale: 'linear-gradient(180deg, #eef5ff 0%, #f8fbff 48%, #ffffff 100%)',
      restaurant: 'linear-gradient(180deg, #f1f7ff 0%, #f9fcff 48%, #ffffff 100%)',
      packageManagement: 'linear-gradient(180deg, #edf6ff 0%, #f7fbff 48%, #ffffff 100%)',
    };
    const fiscalKeys = (t(`salesSystems.modules.${mod.key}.fiscalBrands`, {
      returnObjects: true,
    }) as FiscalBrandKey[]) || [];
    const peripheralKeys = (t(`salesSystems.modules.${mod.key}.peripherals`, {
      returnObjects: true,
    }) as PeripheralKey[]) || [];
    const pavoModeKeys = mod.hasPavoModes
      ? ((t(`salesSystems.modules.${mod.key}.pavoModes`, {
          returnObjects: true,
        }) as PavoModeKey[]) || [])
      : [];
    const highlights = (t(`salesSystems.modules.${mod.key}.highlights`, {
      returnObjects: true,
    }) as string[]) || [];

    return (
      <Box
        key={mod.key}
        id={mod.anchorId}
        sx={{
          py: 8,
          background: moduleBackgroundMap[mod.key],
          borderTop: '1px solid #dfeafc',
          borderBottom: '1px solid #e8f1ff',
        }}
      >
        <Container maxWidth="lg">
          {/* Modül Başlığı */}
          {moduleHeroImage ? (
            <Grid container spacing={3} alignItems="center" sx={{ mb: 4 }}>
              <Grid item xs={12} md={7}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' }, mb: 2, gap: 2 }}>
                  <Avatar
                    sx={{
                      backgroundColor: mod.color,
                      width: 56,
                      height: 56,
                    }}
                  >
                    {mod.icon}
                  </Avatar>
                  <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 700,
                        color: BRAND.colors.primary,
                        fontSize: { xs: '1.6rem', md: '2.2rem' },
                        lineHeight: 1.1,
                      }}
                    >
                      {t(`salesSystems.modules.${mod.key}.title`)}
                    </Typography>
                    <Chip
                      size="small"
                      label={t(`salesSystems.modules.${mod.key}.tag`)}
                      sx={{
                        mt: 0.5,
                        backgroundColor: `${mod.color}15`,
                        color: mod.color,
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Box>
                <Typography
                  variant="subtitle1"
                  textAlign={{ xs: 'center', md: 'left' }}
                  sx={{ color: '#444', fontWeight: 600, mb: 1 }}
                >
                  {t(`salesSystems.modules.${mod.key}.subtitle`)}
                </Typography>
                <Typography
                  variant="body1"
                  textAlign={{ xs: 'center', md: 'left' }}
                  sx={{ color: '#555', maxWidth: { xs: 900, md: 640 }, mb: 0, lineHeight: 1.7 }}
                >
                  {t(`salesSystems.modules.${mod.key}.description`)}
                </Typography>
              </Grid>

              <Grid item xs={12} md={5}>
                {mod.key === 'restaurant' ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      gap: 1.5,
                      width: '100%',
                      minHeight: 240,
                    }}
                  >
                    <Box
                      component="img"
                      src="/assets/restoran_iphone.png"
                      alt="Restoran mobil ekranı"
                      sx={{
                        width: 110,
                        maxHeight: 250,
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 10px 18px rgba(20, 58, 125, 0.24))',
                      }}
                    />
                    <Box
                      component="img"
                      src="/assets/restoran_pc.png"
                      alt="Restoran masaüstü ekranı"
                      sx={{
                        width: '100%',
                        maxWidth: 320,
                        maxHeight: 210,
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 14px 24px rgba(20, 58, 125, 0.28))',
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    component="img"
                    src={moduleHeroImage}
                    alt={mod.key === 'packageManagement' ? 'Paket yönetimi ekranı' : 'Hızlı Satış ekranı'}
                    sx={{
                      width: '100%',
                      maxWidth: mod.key === 'packageManagement' ? 390 : 360,
                      maxHeight: mod.key === 'packageManagement' ? 230 : 220,
                      display: 'block',
                      mx: 'auto',
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 14px 24px rgba(20, 58, 125, 0.28))',
                    }}
                  />
                )}
              </Grid>
            </Grid>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Avatar
                  sx={{
                    backgroundColor: mod.color,
                    width: 56,
                    height: 56,
                  }}
                >
                  {mod.icon}
                </Avatar>
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      color: BRAND.colors.primary,
                      fontSize: { xs: '1.6rem', md: '2.2rem' },
                      lineHeight: 1.1,
                    }}
                  >
                    {t(`salesSystems.modules.${mod.key}.title`)}
                  </Typography>
                  <Chip
                    size="small"
                    label={t(`salesSystems.modules.${mod.key}.tag`)}
                    sx={{
                      mt: 0.5,
                      backgroundColor: `${mod.color}15`,
                      color: mod.color,
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Box>
              <Typography
                variant="subtitle1"
                textAlign="center"
                sx={{ color: '#444', fontWeight: 600, mb: 1 }}
              >
                {t(`salesSystems.modules.${mod.key}.subtitle`)}
              </Typography>
              <Typography
                variant="body1"
                textAlign="center"
                sx={{ color: '#555', maxWidth: 900, mx: 'auto', mb: 5, lineHeight: 1.7 }}
              >
                {t(`salesSystems.modules.${mod.key}.description`)}
              </Typography>
            </>
          )}

          <Grid container spacing={3}>
            {/* Highlights */}
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  border: '1px solid #dbe7fb',
                  borderRadius: 2.5,
                  background: 'linear-gradient(180deg, #ffffff 0%, #f3f8ff 100%)',
                  boxShadow: '0 8px 18px rgba(21, 101, 192, 0.08)',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: BRAND.colors.primary }}>
                  {t(`salesSystems.modules.${mod.key}.highlightsTitle`)}
                </Typography>
                <List dense disablePadding>
                  {highlights.map((h, i) => (
                    <ListItem key={i} sx={{ py: 0.5, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckCircle sx={{ color: mod.color, fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={h}
                        primaryTypographyProps={{ fontSize: '0.92rem', color: '#333' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* Devices columns */}
            <Grid item xs={12} md={7}>
              <Stack spacing={3}>
                {/* Yazarkasalar */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: '#222', mb: 1.5, letterSpacing: 0.3 }}
                  >
                    {t(`salesSystems.modules.${mod.key}.fiscalBrandsTitle`)}
                  </Typography>
                  <Grid container spacing={1.5}>
                    {fiscalKeys.map((bk) => {
                      const brand = FISCAL_BRAND_MAP[bk];
                      if (!brand) return null;
                      return (
                        <Grid item xs={12} sm={6} key={bk}>
                          <Paper
                            elevation={0}
                            onClick={() => scrollToSection(brand.anchorId)}
                            sx={{
                              p: 2,
                              minHeight: 132,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              textAlign: 'center',
                              gap: 1,
                              border: '1px solid #e2e8f5',
                              borderRadius: 3,
                              background: 'linear-gradient(180deg, #ffffff 0%, #f2f8ff 100%)',
                              boxShadow: '0 8px 18px rgba(21, 101, 192, 0.09)',
                              cursor: 'pointer',
                              transition: 'all 0.25s ease',
                              '&:hover': {
                                borderColor: brand.color,
                                background: `linear-gradient(180deg, ${brand.color}14 0%, #ffffff 100%)`,
                                boxShadow: `0 12px 24px ${brand.color}26`,
                                transform: 'translateY(-4px)',
                              },
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: brand.color,
                                width: 46,
                                height: 46,
                                boxShadow: `0 8px 16px ${brand.color}40`,
                              }}
                            >
                              {brand.icon}
                            </Avatar>
                            <Box sx={{ width: '100%' }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 800, color: '#1f2937', lineHeight: 1.3, mb: 0.3 }}
                              >
                                {t(`salesSystems.fiscal.brands.${bk}.title`)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#556070',
                                  display: '-webkit-box',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  lineHeight: 1.35,
                                  px: 0.5,
                                }}
                              >
                                {t(`salesSystems.fiscal.brands.${bk}.subtitle`)}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>

                {/* Pavo Modes (Sadece restoran modülünde) */}
                {pavoModeKeys.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: '#222', mb: 1.5, letterSpacing: 0.3 }}
                    >
                      {t(`salesSystems.modules.${mod.key}.pavoModesTitle`)}
                    </Typography>
                    <Grid container spacing={1.5}>
                      {pavoModeKeys.map((pk) => {
                        const pmode = PAVO_MODE_MAP[pk];
                        if (!pmode) return null;
                        return (
                          <Grid item xs={12} sm={6} key={pk}>
                            <Paper
                              elevation={0}
                              onClick={() => scrollToSection(`pavo-${pk}`)}
                              sx={{
                                p: 2,
                                minHeight: 132,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                gap: 1,
                              border: '1px solid #dbe7fb',
                              borderRadius: 2.5,
                              background: 'linear-gradient(180deg, #ffffff 0%, #f4f8ff 100%)',
                              boxShadow: '0 8px 18px rgba(21, 101, 192, 0.09)',
                                cursor: 'pointer',
                                transition: 'all 0.25s ease',
                                '&:hover': {
                                  borderColor: pmode.color,
                                background: `linear-gradient(180deg, ${pmode.color}12 0%, #ffffff 100%)`,
                                boxShadow: `0 12px 24px ${pmode.color}26`,
                                  transform: 'translateY(-4px)',
                                },
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: pmode.color,
                                  width: 46,
                                  height: 46,
                                  boxShadow: `0 8px 16px ${pmode.color}40`,
                                }}
                              >
                                {pmode.icon}
                              </Avatar>
                              <Box sx={{ width: '100%' }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 800, color: '#1f2937', lineHeight: 1.3, mb: 0.3 }}
                                >
                                  {t(`salesSystems.pavoModes.modes.${pk}.title`)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#556070',
                                    display: '-webkit-box',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    lineHeight: 1.35,
                                    px: 0.5,
                                  }}
                                >
                                  {t(`salesSystems.pavoModes.modes.${pk}.tag`)}
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}

                {/* Periferaller */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: '#222', mb: 1.5, letterSpacing: 0.3 }}
                  >
                    {t(`salesSystems.modules.${mod.key}.peripheralsTitle`)}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {peripheralKeys.map((pk) => {
                      const peri = PERIPHERAL_MAP[pk];
                      if (!peri) return null;
                      return (
                        <Chip
                          key={pk}
                          icon={peri.icon as React.ReactElement}
                          label={t(`salesSystems.peripherals.items.${pk}.title`)}
                          onClick={() => scrollToSection(`peripheral-${pk}`)}
                          sx={{
                            backgroundColor: peri.color,
                            color: 'white',
                            fontWeight: 600,
                            px: 0.5,
                            '& .MuiChip-icon': { color: 'white' },
                            '&:hover': { backgroundColor: peri.color, opacity: 0.85 },
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  };

  return (
    <PageContainer>
      <Helmet>
        <title>{t('salesSystems.meta.title')}</title>
        <meta name="description" content={t('salesSystems.meta.description')} />
        <meta name="keywords" content={t('salesSystems.meta.keywords')} />
      </Helmet>

      <ScrollProgressBar
        sx={{
          '&::after': {
            width: `${scrollProgress}%`,
          },
        }}
      />

      <Header />

      <ContentWrapper>
        {/* Hero Section */}
        <HeroSection>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.1rem', sm: '2.9rem', md: '3.35rem' },
                    fontWeight: 800,
                    mb: 2,
                    background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {t('salesSystems.hero.title')}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.22rem', md: '1.35rem' },
                    fontWeight: 400,
                    mb: 4,
                    opacity: 0.9,
                    maxWidth: { xs: '900px', md: '620px' },
                    mx: { xs: 'auto', md: 0 },
                  }}
                >
                  {t('salesSystems.hero.subtitle')}
                </Typography>

                <Box sx={{ mb: 4 }}>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      mr: 2,
                      mb: { xs: 2, sm: 0 },
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      backgroundColor: 'white',
                      color: BRAND.colors.primary,
                      '&:hover': { backgroundColor: '#f5f5f5', transform: 'translateY(-2px)' },
                    }}
                    onClick={() => openRoleSelection()}
                  >
                    {t('salesSystems.hero.tryFreeButton')}
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      backgroundColor: 'white',
                      color: BRAND.colors.primary,
                      '&:hover': { backgroundColor: '#f5f5f5', transform: 'translateY(-2px)' },
                    }}
                    onClick={() => navigate('/contact')}
                  >
                    {t('salesSystems.hero.demoButton')}
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                <Box sx={{ width: '100%', maxWidth: 460, mx: 'auto', transform: 'translateY(-42px)' }}>
                  <Typography
                    component="div"
                    sx={{
                      fontWeight: 700,
                      color: 'white',
                      minHeight: { md: 86, lg: 94 },
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      textAlign: 'center',
                      fontSize: { md: '2rem', lg: '2.35rem' },
                      lineHeight: 1.12,
                      letterSpacing: 0.2,
                      textShadow: '0 3px 18px rgba(0, 0, 0, 0.45)',
                      mb: 1.5,
                    }}
                  >
                    {getHeroSlideTitle(HERO_DEVICE_SLIDES[heroSlideIndex])}
                  </Typography>

                  <Box
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: { md: 290, lg: 315 },
                    }}
                  >
                    <Box
                      onClick={goToPrevHeroSlide}
                      sx={{
                        position: 'absolute',
                        left: -10,
                        zIndex: 2,
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.18)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                      }}
                    >
                      <NavigateBefore sx={{ color: 'white', fontSize: 22 }} />
                    </Box>

                    <Box
                      component="img"
                      src={HERO_DEVICE_SLIDES[heroSlideIndex].src}
                      alt={getHeroSlideTitle(HERO_DEVICE_SLIDES[heroSlideIndex])}
                      sx={{
                        width: '86%',
                        height: '86%',
                        objectFit: 'contain',
                        objectPosition: 'center 35%',
                        filter: 'drop-shadow(0 18px 28px rgba(0, 0, 0, 0.35))',
                        opacity: heroImageVisible ? 1 : 0,
                        transition: 'opacity 620ms cubic-bezier(0.22, 1, 0.36, 1)',
                      }}
                    />

                    <Box
                      onClick={goToNextHeroSlide}
                      sx={{
                        position: 'absolute',
                        right: -10,
                        zIndex: 2,
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.18)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                      }}
                    >
                      <NavigateNext sx={{ color: 'white', fontSize: 22 }} />
                    </Box>
                  </Box>

                  <Box sx={{ mt: 1.1, display: 'flex', justifyContent: 'center', gap: 0.8 }}>
                    {HERO_DEVICE_SLIDES.map((_, idx) => (
                      <Box
                        key={idx}
                        onClick={() => {
                          changeHeroSlide(idx, true);
                        }}
                        sx={{
                          width: idx === heroSlideIndex ? 22 : 8,
                          height: 8,
                          borderRadius: 999,
                          backgroundColor:
                            idx === heroSlideIndex ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.45)',
                          transition: 'all 0.25s ease',
                          cursor: 'pointer',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Quick Navigation */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2.5,
                flexWrap: 'wrap',
                mb: 4,
              }}
            >
              {MODULES.map((mod) => (
                <Button
                  key={mod.key}
                  variant="text"
                  size="small"
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                    fontWeight: 700,
                    px: 1.2,
                    py: 0.6,
                    '&:hover': { color: 'white' },
                  }}
                  onClick={() => scrollToSection(mod.anchorId)}
                >
                  {t(`salesSystems.modules.${mod.key}.title`)}
                </Button>
              ))}
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' },
                }}
                onClick={() => scrollToSection('fiscal-brands')}
              >
                {t('salesSystems.nav.fiscal')}
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' },
                }}
                onClick={() => scrollToSection('faq')}
              >
                {t('salesSystems.nav.faq')}
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' },
                }}
                onClick={() => scrollToSection('cta')}
              >
                {t('salesSystems.nav.getStarted')}
              </Button>
            </Box>

            {/* Stats Section */}
            <StatsSection>
              <Grid container spacing={4}>
                {stats.map((stat, index) => (
                  <Grid item xs={6} md={3} key={index}>
                    <Box textAlign="center">
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 800,
                          color: 'white',
                          fontSize: { xs: '1rem', md: '1.8rem' },
                        }}
                      >
                        {stat.number}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: { xs: '0.7rem', md: '0.8rem' },
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </StatsSection>
          </Container>
        </HeroSection>

        {/* MODÜLLER ÖZETİ - 3 büyük kart */}
        <Box
          id="modules"
          sx={{
            py: 8,
            background: 'linear-gradient(180deg, #edf5ff 0%, #f8fbff 48%, #ffffff 100%)',
            borderTop: '1px solid #dfeafc',
            borderBottom: '1px solid #e8f1ff',
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              textAlign="center"
              sx={{
                mb: 2,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '1.8rem', md: '2.6rem' },
              }}
            >
              {t('salesSystems.modules.title')}
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              sx={{
                mb: 6,
                color: '#555',
                maxWidth: 900,
                mx: 'auto',
                fontSize: { xs: '0.95rem', md: '1.05rem' },
              }}
            >
              {t('salesSystems.modules.subtitle')}
            </Typography>

            <Grid container spacing={4}>
              {MODULES.map((mod) => (
                <Grid item xs={12} md={4} key={mod.key}>
                  <FeatureCard
                    onClick={() => scrollToSection(mod.anchorId)}
                    sx={{
                      cursor: 'pointer',
                      border: '1px solid #dbe7fb',
                      borderRadius: 3,
                      background: 'linear-gradient(180deg, #ffffff 0%, #f2f8ff 100%)',
                      boxShadow: '0 10px 22px rgba(21, 101, 192, 0.09)',
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Avatar
                        sx={{
                          backgroundColor: mod.color,
                          width: 64,
                          height: 64,
                          mb: 2,
                        }}
                      >
                        {mod.icon}
                      </Avatar>
                      <Chip
                        size="small"
                        label={t(`salesSystems.modules.${mod.key}.tag`)}
                        sx={{
                          mb: 1.5,
                          backgroundColor: `${mod.color}15`,
                          color: mod.color,
                          fontWeight: 600,
                        }}
                      />
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', mb: 1 }}>
                        {t(`salesSystems.modules.${mod.key}.title`)}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: '#444', mb: 1, fontWeight: 600 }}>
                        {t(`salesSystems.modules.${mod.key}.subtitle`)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
                        {t(`salesSystems.modules.${mod.key}.description`)}
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        sx={{ mt: 2, color: mod.color, fontWeight: 700 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          scrollToSection(mod.anchorId);
                        }}
                      >
                        {t('salesSystems.flyout.viewAll')} →
                      </Button>
                    </CardContent>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* MODÜL DETAY BÖLÜMLERİ */}
        {MODULES.map(renderModuleSection)}

        {/* YAZARKASA MARKA DETAYLARI - referans / arşiv */}
        <Box
          id="fiscal-brands"
          sx={{
            py: 8,
            background: 'linear-gradient(180deg, #eef6ff 0%, #f8fbff 48%, #ffffff 100%)',
            borderTop: '1px solid #dfeafc',
            borderBottom: '1px solid #e8f1ff',
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={3} alignItems="center" sx={{ mb: 5 }}>
              <Grid item xs={12} md={7}>
                <Typography
                  variant="h2"
                  textAlign={{ xs: 'center', md: 'left' }}
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    color: BRAND.colors.primary,
                    fontSize: { xs: '1.8rem', md: '2.6rem' },
                  }}
                >
                  {t('salesSystems.fiscal.title')}
                </Typography>
                <Typography
                  variant="body1"
                  textAlign={{ xs: 'center', md: 'left' }}
                  sx={{
                    color: '#555',
                    maxWidth: { xs: 900, md: 640 },
                    mx: { xs: 'auto', md: 0 },
                    fontSize: { xs: '0.95rem', md: '1.05rem' },
                  }}
                >
                  {t('salesSystems.fiscal.subtitle')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box
                  component="img"
                  src="/assets/pavo-n86.png"
                  alt="PavoPay POS cihazı"
                  sx={{
                    width: '100%',
                    maxWidth: 220,
                    maxHeight: 290,
                    display: 'block',
                    mx: 'auto',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 14px 24px rgba(20, 58, 125, 0.28))',
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              {FISCAL_BRANDS.map((brand) => {
                const features = (t(`salesSystems.fiscal.brands.${brand.key}.features`, {
                  returnObjects: true,
                }) as string[]) || [];
                return (
                  <Grid item xs={12} md={6} lg={4} key={brand.key} id={brand.anchorId}>
                    <FeatureCard
                      sx={{
                        border: '1px solid #dbe7fb',
                        borderRadius: 3,
                        background: 'linear-gradient(180deg, #ffffff 0%, #f2f8ff 100%)',
                        boxShadow: '0 10px 22px rgba(21, 101, 192, 0.09)',
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar sx={{ backgroundColor: brand.color, width: 48, height: 48, mr: 2 }}>
                            {brand.icon}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#222' }}>
                              {t(`salesSystems.fiscal.brands.${brand.key}.title`)}
                            </Typography>
                            <Chip
                              size="small"
                              label={t(`salesSystems.fiscal.brands.${brand.key}.tag`)}
                              sx={{
                                mt: 0.5,
                                backgroundColor: `${brand.color}15`,
                                color: brand.color,
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#666', mb: 1.5, lineHeight: 1.6 }}>
                          {t(`salesSystems.fiscal.brands.${brand.key}.description`)}
                        </Typography>
                        <List dense disablePadding>
                          {features.map((feat, i) => (
                            <ListItem key={i} sx={{ py: 0.25, px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 24 }}>
                                <CheckCircle sx={{ color: brand.color, fontSize: 16 }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={feat}
                                primaryTypographyProps={{ fontSize: '0.82rem' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                        <Button
                          variant="text"
                          size="small"
                          sx={{ mt: 1, color: brand.color, fontWeight: 700 }}
                          onClick={() => {
                            setFaqTab(brand.key);
                            scrollToSection('faq');
                          }}
                        >
                          {t('salesSystems.nav.faq')} →
                        </Button>
                      </CardContent>
                    </FeatureCard>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
        </Box>

        {/* PAVO MODES (referans için, restoran modülünden link veriliyor) */}
        <Box
          id="pavo-modes"
          sx={{
            py: 8,
            background: 'linear-gradient(180deg, #edf6ff 0%, #f8fcff 48%, #ffffff 100%)',
            borderTop: '1px solid #dfeafc',
            borderBottom: '1px solid #e8f1ff',
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              textAlign="center"
              sx={{
                mb: 2,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '1.8rem', md: '2.6rem' },
              }}
            >
              {t('salesSystems.pavoModes.title')}
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              sx={{
                mb: 6,
                color: '#555',
                maxWidth: 900,
                mx: 'auto',
                fontSize: { xs: '0.95rem', md: '1.05rem' },
              }}
            >
              {t('salesSystems.pavoModes.subtitle')}
            </Typography>

            <Grid container spacing={4}>
              {PAVO_MODES.map((mode) => {
                const features = (t(`salesSystems.pavoModes.modes.${mode.key}.features`, {
                  returnObjects: true,
                }) as string[]) || [];
                return (
                  <Grid item xs={12} md={4} key={mode.key} id={`pavo-${mode.key}`}>
                    <FeatureCard
                      sx={{
                        border: '1px solid #dbe7fb',
                        borderRadius: 3,
                        background: 'linear-gradient(180deg, #ffffff 0%, #f2f8ff 100%)',
                        boxShadow: '0 10px 22px rgba(21, 101, 192, 0.09)',
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Avatar sx={{ backgroundColor: mode.color, width: 64, height: 64, mb: 2 }}>
                          {mode.icon}
                        </Avatar>
                        <Chip
                          size="small"
                          label={t(`salesSystems.pavoModes.modes.${mode.key}.tag`)}
                          sx={{
                            mb: 1.5,
                            backgroundColor: `${mode.color}15`,
                            color: mode.color,
                            fontWeight: 600,
                          }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#222', mb: 1 }}>
                          {t(`salesSystems.pavoModes.modes.${mode.key}.title`)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', mb: 2, lineHeight: 1.6 }}>
                          {t(`salesSystems.pavoModes.modes.${mode.key}.description`)}
                        </Typography>
                        <List dense disablePadding>
                          {features.map((f, i) => (
                            <ListItem key={i} sx={{ py: 0.25, px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 24 }}>
                                <CheckCircle sx={{ color: mode.color, fontSize: 16 }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={f}
                                primaryTypographyProps={{ fontSize: '0.82rem' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </FeatureCard>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
        </Box>

        {/* PERİFERAL DETAYLAR */}
        <Box
          id="peripherals"
          sx={{
            py: 8,
            background: 'linear-gradient(180deg, #eef6ff 0%, #f8fbff 48%, #ffffff 100%)',
            borderTop: '1px solid #dfeafc',
            borderBottom: '1px solid #e8f1ff',
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              textAlign="center"
              sx={{
                mb: 2,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '1.8rem', md: '2.6rem' },
              }}
            >
              {t('salesSystems.peripherals.title')}
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              sx={{
                mb: 6,
                color: '#555',
                maxWidth: 900,
                mx: 'auto',
                fontSize: { xs: '0.95rem', md: '1.05rem' },
              }}
            >
              {t('salesSystems.peripherals.subtitle')}
            </Typography>

            <Grid container spacing={3}>
              {PERIPHERALS.map((p) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={p.key} id={`peripheral-${p.key}`}>
                  <FeatureCard
                    sx={{
                      border: '1px solid #dbe7fb',
                      borderRadius: 3,
                      background: 'linear-gradient(180deg, #ffffff 0%, #f2f8ff 100%)',
                      boxShadow: '0 10px 22px rgba(21, 101, 192, 0.09)',
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                      <Avatar
                        sx={{
                          backgroundColor: p.color,
                          width: 56,
                          height: 56,
                          mx: 'auto',
                          mb: 2,
                        }}
                      >
                        {p.icon}
                      </Avatar>
                      <Chip
                        size="small"
                        label={t(`salesSystems.peripherals.items.${p.key}.tag`)}
                        sx={{
                          mb: 1.5,
                          backgroundColor: `${p.color}15`,
                          color: p.color,
                          fontWeight: 600,
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, color: '#222', fontSize: '1rem', mb: 1 }}
                      >
                        {t(`salesSystems.peripherals.items.${p.key}.title`)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#666', lineHeight: 1.5, fontSize: '0.85rem' }}
                      >
                        {t(`salesSystems.peripherals.items.${p.key}.description`)}
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* SSS */}
        <Box
          id="faq"
          sx={{
            py: 8,
            background: 'linear-gradient(180deg, #edf6ff 0%, #f8fcff 45%, #ffffff 100%)',
            borderTop: '1px solid #dfeafc',
            borderBottom: '1px solid #e8f1ff',
          }}
        >
          <Container maxWidth="md">
            <Typography
              variant="h2"
              textAlign="center"
              sx={{
                mb: 2,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '1.8rem', md: '2.6rem' },
              }}
            >
              {t('salesSystems.faq.title')}
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              sx={{ mb: 4, color: '#555' }}
            >
              {t('salesSystems.faq.subtitle')}
            </Typography>

            <Tabs
              value={faqTab}
              onChange={(_, v) => setFaqTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                mb: 3,
                p: 1,
                borderRadius: 2.5,
                background: 'linear-gradient(180deg, #ffffff 0%, #f2f8ff 100%)',
                border: '1px solid #dbe7fb',
                boxShadow: '0 8px 18px rgba(21, 101, 192, 0.08)',
                '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' },
              }}
            >
              {FAQ_BRANDS.map((brand) => (
                <Tab
                  key={brand.key}
                  value={brand.key}
                  label={t(`salesSystems.fiscal.brands.${brand.key}.title`)}
                />
              ))}
            </Tabs>

            {FAQ_BRANDS.map((brand) => {
              if (brand.key !== faqTab) return null;
              const items = (t(`salesSystems.faq.groups.${brand.key}`, {
                returnObjects: true,
              }) as { question: string; answer: string }[]) || [];
              return (
                <Box key={brand.key}>
                  {items.map((item, idx) => (
                    <Accordion
                      key={idx}
                      sx={{
                        mb: 1.5,
                        border: '1px solid #dbe7fb',
                        borderRadius: 2,
                        background: 'linear-gradient(180deg, #ffffff 0%, #f4f9ff 100%)',
                        boxShadow: '0 8px 18px rgba(21, 101, 192, 0.08)',
                        '&:before': { display: 'none' },
                      }}
                      disableGutters
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {idx + 1}) {item.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                          {item.answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              );
            })}
          </Container>
        </Box>

        {/* CTA */}
        <Box id="cta" sx={{ py: 8, backgroundColor: BRAND.colors.primary, color: 'white' }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
              }}
            >
              {t('salesSystems.cta.title')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                opacity: 0.9,
                fontSize: { xs: '1rem', md: '1.25rem' },
              }}
            >
              {t('salesSystems.cta.subtitle')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                backgroundColor: 'white',
                color: BRAND.colors.primary,
                '&:hover': { backgroundColor: '#f5f5f5', transform: 'translateY(-2px)' },
              }}
              onClick={() => navigate('/contact')}
            >
              {t('salesSystems.cta.button')}
            </Button>
          </Container>
        </Box>

        <Footer />
      </ContentWrapper>

      <PublicAiChatWidget />

    </PageContainer>
  );
};

export default SalesSystems;
